import React from 'react';
import PropTypes from 'prop-types';
import { Link as RouterLink } from 'react-router-dom';
import {
  alpha, Box, Button, Chip, CircularProgress, Paper, Skeleton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography,
} from '@mui/material';
import {
  BubbleChart as BubbleChartIcon,
  Spa as SpaIcon,
  GpsFixed as GpsFixedIcon,
  RocketLaunch as RocketLaunchIcon,
  Visibility as VisibilityIcon,
  Bolt as BoltIcon,
  ArrowForward as ArrowForwardIcon,
  CheckRounded as CheckRoundedIcon,
} from '@mui/icons-material';
import AIConfidenceBadge from '../AIConfidenceBadge';
import {
  C,
  SectionCard,
  SectionHeader,
  MetricCell,
  candidateRowPropType,
  stageSkeletonSlots,
  tableSkeletonRows,
  tableSkeletonColumns,
  thSx,
  tdSx,
  trSx,
  fmtScore,
  fmtPct,
  fmtInteger,
  localizedText,
  toneColor,
  getTierMeta,
  getDeltaColor,
  getHistoryPickTone,
  reliabilityTone,
  getSkeletonWidth,
  hasValue,
  formatIfPresent,
  metricGoodIfPresent,
  absorptionMetricLabel,
  sectorDisplay,
} from './shared';

export const EARLY_ACCUMULATION_STAGE_META = {
  FORMING: {
    icon: SpaIcon,
    color: '#0ea5e9',
  },
  MATURE: {
    icon: GpsFixedIcon,
    color: C.blue,
  },
  BREAKOUT: {
    icon: RocketLaunchIcon,
    color: C.emerald,
  },
  NEAR_MISS: {
    icon: VisibilityIcon,
    color: C.amber,
  },
};

export const buildStageMeta = (t) => ({
  FORMING: {
    label: t('earlyAccV2.stages.FORMING.label'),
    hint: t('earlyAccV2.stages.FORMING.hint'),
    Icon: EARLY_ACCUMULATION_STAGE_META.FORMING.icon,
    color: EARLY_ACCUMULATION_STAGE_META.FORMING.color,
    desc: t('earlyAccV2.stages.FORMING.desc'),
  },
  MATURE: {
    label: t('earlyAccV2.stages.MATURE.label'),
    hint: t('earlyAccV2.stages.MATURE.hint'),
    Icon: EARLY_ACCUMULATION_STAGE_META.MATURE.icon,
    color: EARLY_ACCUMULATION_STAGE_META.MATURE.color,
    desc: t('earlyAccV2.stages.MATURE.desc'),
  },
  BREAKOUT: {
    label: t('earlyAccV2.stages.BREAKOUT.label'),
    hint: t('earlyAccV2.stages.BREAKOUT.hint'),
    Icon: EARLY_ACCUMULATION_STAGE_META.BREAKOUT.icon,
    color: EARLY_ACCUMULATION_STAGE_META.BREAKOUT.color,
    desc: t('earlyAccV2.stages.BREAKOUT.desc'),
  },
  NEAR_MISS: {
    label: t('earlyAccV2.stages.NEAR_MISS.label'),
    hint: t('earlyAccV2.stages.NEAR_MISS.hint'),
    Icon: EARLY_ACCUMULATION_STAGE_META.NEAR_MISS.icon,
    color: EARLY_ACCUMULATION_STAGE_META.NEAR_MISS.color,
    desc: t('earlyAccV2.stages.NEAR_MISS.desc'),
  },
});

export function HistorySection({ history, historyLoading, locale, t }) {
  const items = (history || []).slice().sort((a, b) => (a.score_date < b.score_date ? 1 : -1));

  return (
    <SectionCard>
      <Box sx={{ px: 2.5, py: 1.4, borderBottom: `1px solid ${C.border}` }}>
        <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: C.ink }}>{t('earlyAccV2.history.title')}</Typography>
        <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8', mt: 0.2 }}>{t('earlyAccV2.history.subtitle')}</Typography>
      </Box>
      <Box sx={{ px: 2.5, py: 1.6, maxHeight: 480, overflowY: 'auto' }}>
        {items.length === 0 && !historyLoading && (
          <Box sx={{ textAlign: 'center', py: 4, color: '#94a3b8', fontSize: '0.78rem' }}>{t('earlyAccV2.history.empty')}</Box>
        )}
        {items.map((row, idx) => {
          const tone = reliabilityTone(row.reliability_score);
          const picks = (row.top_picks || []).slice(0, 8);
          const dateObj = new Date(row.score_date);
          const dateLabel = dateObj.toLocaleDateString(locale === 'vi-VN' ? 'vi-VN' : 'en-US', { day: '2-digit', month: 'short', weekday: 'short' });
          const isLast = idx === items.length - 1;
          return (
            <Box key={row.score_date} sx={{ display: 'grid', gridTemplateColumns: '20px 1fr', columnGap: 2 }}>
              <Box sx={{ position: 'relative', display: 'flex', justifyContent: 'center' }}>
                {!isLast && <Box sx={{ position: 'absolute', top: 18, bottom: -8, width: '1px', bgcolor: '#e2e8f0' }} />}
                <Box sx={{ position: 'relative', mt: '8px', width: 9, height: 9, borderRadius: '50%', bgcolor: tone, boxShadow: `0 0 0 3px ${alpha(tone, 0.15)}` }} />
              </Box>
              <Box sx={{ pb: isLast ? 0 : 1.8, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, flexWrap: 'wrap' }}>
                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: C.ink, textTransform: 'capitalize' }}>{dateLabel}</Typography>
                  <Typography sx={{ fontSize: '0.85rem', fontWeight: 800, color: tone, lineHeight: 1 }}>{fmtScore(row.reliability_score, locale, 1)}</Typography>
                  <Typography sx={{ fontSize: '0.68rem', color: '#64748b' }}>{row.total_candidates} signals · avg {fmtScore(row.avg_score, locale, 1)}</Typography>
                </Box>
                {picks.length > 0 && (
                  <Box sx={{ mt: 0.6, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {picks.map((pick) => {
                      const pickTone = getHistoryPickTone(pick);
                      return (
                        <Box
                          key={`${row.score_date}-${pick.ticker}`}
                          component={RouterLink}
                          to={`/stocks/${pick.ticker}`}
                          sx={{
                            display: 'inline-flex', alignItems: 'center', gap: 0.45,
                            height: 20, px: 0.7, borderRadius: 0.5,
                            border: '1px solid #e2e8f0', bgcolor: '#fff',
                            textDecoration: 'none', transition: 'all 0.12s',
                            '&:hover': { borderColor: pickTone, bgcolor: alpha(pickTone, 0.05) },
                          }}
                        >
                          <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: pickTone }} />
                          <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: C.ink }}>{pick.ticker}</Typography>
                          <Typography sx={{ fontSize: '0.62rem', color: '#94a3b8', fontWeight: 600 }}>{Math.round(pick.score)}</Typography>
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    </SectionCard>
  );
}

HistorySection.propTypes = {
  history: PropTypes.arrayOf(PropTypes.shape({
    score_date: PropTypes.string,
    reliability_score: PropTypes.number,
    total_candidates: PropTypes.number,
    avg_score: PropTypes.number,
    top_picks: PropTypes.arrayOf(PropTypes.shape({
      ticker: PropTypes.string,
      score: PropTypes.number,
      is_breakout: PropTypes.bool,
      is_early: PropTypes.bool,
      near_miss: PropTypes.bool,
    })),
  })),
  historyLoading: PropTypes.bool,
  locale: PropTypes.string.isRequired,
  t: PropTypes.func.isRequired,
};

function StockCard({ row, stage, stageMeta, locale, language, onSelectRow, t }) {
  const meta = stageMeta;
  const change = row.change || {};
  const delta = change.delta;
  const isNew = change.is_new;
  const score = Number(row.early_accumulation_score) || 0;
  const distRes = row.price?.dist_from_resistance;
  const range20 = row.metrics?.range20;
  const lead = row.metrics?.base_readiness_lead;
  const accum = row.metrics?.accumulation_ratio;
  const rs20 = row.metrics?.rs20;
  const strength = row.strength?.strength_score;
  const absorptionMetric = row.display?.metrics?.absorption;
  const absorptionText = localizedText(absorptionMetric?.text, language);
  const absorptionColor = toneColor(absorptionMetric?.tone);

  return (
    <Box
      onClick={() => onSelectRow(row)}
      sx={{
        p: 1.4,
        borderRadius: 1.2,
        border: `1px solid ${C.border}`,
        bgcolor: '#fff',
        cursor: 'pointer',
        transition: 'all 0.15s ease',
        position: 'relative',
        '&:hover': {
          borderColor: meta.color,
          boxShadow: `0 4px 12px ${alpha(meta.color, 0.12)}`,
          transform: 'translateY(-1px)',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1, mb: 0.6 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.7, minWidth: 0 }}>
          <Typography
            component={RouterLink}
            to={`/stocks/${row.ticker}`}
            onClick={(event) => event.stopPropagation()}
            sx={{ fontSize: '0.95rem', fontWeight: 800, color: C.ink, textDecoration: 'none', letterSpacing: '0.01em', '&:hover': { color: meta.color } }}
          >
            {row.ticker}
          </Typography>
          {isNew && (
            <Box sx={{ px: 0.5, py: 0.05, borderRadius: 0.4, bgcolor: alpha(C.emerald, 0.12), border: `1px solid ${alpha(C.emerald, 0.3)}` }}>
              <Typography sx={{ fontSize: '0.55rem', fontWeight: 800, color: C.emerald, letterSpacing: '0.05em' }}>NEW</Typography>
            </Box>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.4, flexShrink: 0 }}>
          <Typography sx={{ fontSize: '0.95rem', fontWeight: 800, color: meta.color, lineHeight: 1 }}>{score.toFixed(1)}</Typography>
          {delta != null && Math.abs(delta) >= 0.05 && (
            <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: delta >= 0 ? C.emerald : C.red }}>
              {delta >= 0 ? '↑' : '↓'}{Math.abs(delta).toFixed(1)}
            </Typography>
          )}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.85 }}>
        <Typography sx={{ fontSize: '0.65rem', color: '#94a3b8', fontWeight: 600, letterSpacing: '0.02em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {sectorDisplay(row, language)}
        </Typography>
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#475569', flexShrink: 0 }}>{fmtInteger(row.price?.close, locale)}</Typography>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.6, mb: 0.5 }}>
        {stage === 'FORMING' && (
          <>
            <MetricCell label={t('earlyAccV2.card.compress')} value={formatIfPresent(range20, (value) => `${(value * 100).toFixed(1)}%`)} good={metricGoodIfPresent(range20, (value) => value < 0.12)} />
            <MetricCell label={t('earlyAccV2.card.lead')} value={formatIfPresent(lead, (value) => `${Math.round(value)}d`)} good={metricGoodIfPresent(lead, (value) => value >= 8)} hint={t('earlyAccV2.card.leadHint')} />
          </>
        )}
        {stage === 'MATURE' && (
          <>
            <MetricCell label={t('earlyAccV2.card.distance')} value={formatIfPresent(distRes, (value) => `${(value * 100).toFixed(1)}%`)} good={metricGoodIfPresent(distRes, (value) => value > -0.03)} />
            <MetricCell label={t('earlyAccV2.card.accumXfer')} value={formatIfPresent(accum, (value) => value.toFixed(2))} good={metricGoodIfPresent(accum, (value) => value >= 1.5)} />
          </>
        )}
        {stage === 'BREAKOUT' && (
          <>
            <MetricCell label={t('earlyAccV2.card.strength')} value={formatIfPresent(strength, (value) => value.toFixed(1))} good={metricGoodIfPresent(strength, (value) => value >= 80)} />
            <MetricCell label="RS 20" value={formatIfPresent(rs20, (value) => `${(value * 100).toFixed(1)}%`)} good={metricGoodIfPresent(rs20, (value) => value > 0.05)} />
          </>
        )}
        {stage === 'NEAR_MISS' && (
          <>
            <MetricCell label={t('earlyAccV2.card.gate')} value={`${row.diagnostics?.criteria_passed || 0}/9`} good={(row.diagnostics?.criteria_passed || 0) >= 7} />
            <MetricCell label={t('earlyAccV2.card.distance')} value={formatIfPresent(distRes, (value) => `${(value * 100).toFixed(1)}%`)} good={metricGoodIfPresent(distRes, (value) => value > -0.05)} />
          </>
        )}
      </Box>

      {absorptionText && (
        <Box sx={{ mt: 0.15, px: 0.8, py: 0.65, borderRadius: 0.9, bgcolor: alpha(absorptionColor, 0.06), border: `1px solid ${alpha(absorptionColor, 0.14)}` }}>
          <Typography sx={{ fontSize: '0.55rem', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {language === 'vi' ? 'Hap thu' : 'Absorption'}
          </Typography>
          <Typography sx={{ mt: 0.2, fontSize: '0.68rem', fontWeight: 700, color: absorptionColor, lineHeight: 1.4 }}>
            {absorptionMetricLabel(absorptionText, absorptionMetric?.value)}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

StockCard.propTypes = {
  row: candidateRowPropType.isRequired,
  stage: PropTypes.string.isRequired,
  stageMeta: PropTypes.shape({ color: PropTypes.string.isRequired }).isRequired,
  locale: PropTypes.string.isRequired,
  language: PropTypes.string.isRequired,
  onSelectRow: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

export function PipelineSection({ promotions, stageMetaMap, pipeline, loading, hasData, onSelectRow, language, locale, t }) {
  return (
    <>
      {promotions.length > 0 && (
        <Box sx={{ p: 1.4, borderRadius: 1.4, border: `1px solid ${alpha(C.emerald, 0.25)}`, background: `linear-gradient(135deg, ${alpha(C.emerald, 0.06)} 0%, ${alpha(C.blue, 0.04)} 100%)` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.85 }}>
            <Typography sx={{ fontSize: '0.78rem', fontWeight: 800, color: C.emerald, letterSpacing: '0.02em', display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
              <BoltIcon sx={{ fontSize: 16 }} />{t('earlyAccV2.promotions.title', { count: promotions.length })}
            </Typography>
            <Typography sx={{ fontSize: '0.7rem', color: '#64748b' }}>{t('earlyAccV2.promotions.subtitle')}</Typography>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.7 }}>
            {promotions.slice(0, 12).map(({ row, from, to }) => {
              const toMeta = stageMetaMap[to] || stageMetaMap.FORMING;
              const ToIcon = toMeta.Icon;
              return (
                <Box
                  key={row.ticker}
                  onClick={() => onSelectRow(row)}
                  sx={{
                    display: 'inline-flex', alignItems: 'center', gap: 0.5,
                    px: 0.9, py: 0.45, borderRadius: 0.7,
                    bgcolor: '#fff', border: `1px solid ${alpha(toMeta.color, 0.3)}`,
                    cursor: 'pointer', transition: 'all 0.15s',
                    '&:hover': { boxShadow: `0 2px 8px ${alpha(toMeta.color, 0.18)}` },
                  }}
                >
                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 800, color: C.ink }}>{row.ticker}</Typography>
                  <Typography sx={{ fontSize: '0.62rem', color: '#94a3b8' }}>{from === 'NEW' ? t('earlyAccV2.promotions.fromNew') : from.toLowerCase()}</Typography>
                  <Typography sx={{ fontSize: '0.7rem', color: toMeta.color, fontWeight: 800, lineHeight: 1, display: 'inline-flex', alignItems: 'center' }}><ArrowForwardIcon sx={{ fontSize: 14 }} /></Typography>
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3 }}>
                    <ToIcon sx={{ fontSize: 13, color: toMeta.color }} />
                    <Typography sx={{ fontSize: '0.65rem', color: toMeta.color, fontWeight: 800, letterSpacing: '0.03em' }}>{to.toLowerCase()}</Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 1 }}>
        {['FORMING', 'MATURE', 'BREAKOUT'].map((stage) => {
          const meta = stageMetaMap[stage];
          const StageIcon = meta.Icon;
          const count = (pipeline[stage] || []).length;
          const isActive = count > 0;
          const isInitialLoad = loading && !hasData;
          return (
            <Box
              key={stage}
              sx={{
                px: 1.4, py: 1.1,
                borderRadius: 1.2,
                border: `1px solid ${isActive ? alpha(meta.color, 0.35) : C.border}`,
                background: isActive ? `linear-gradient(135deg, ${alpha(meta.color, 0.1)} 0%, ${alpha(meta.color, 0.03)} 100%)` : '#fafbfc',
                display: 'flex', alignItems: 'center', gap: 1,
                opacity: isActive || isInitialLoad ? 1 : 0.65,
              }}
            >
              <StageIcon sx={{ fontSize: 18, color: isActive ? meta.color : '#94a3b8' }} />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography sx={{ fontSize: '0.78rem', fontWeight: 800, color: isActive ? C.ink : '#64748b', lineHeight: 1.2 }}>{meta.label}</Typography>
                <Typography sx={{ fontSize: '0.62rem', color: '#94a3b8', fontWeight: 600, lineHeight: 1.3 }}>{meta.hint}</Typography>
              </Box>
              <Box sx={{ px: 0.95, py: 0.25, borderRadius: 0.7, bgcolor: isActive ? meta.color : '#e2e8f0', color: isActive ? '#fff' : '#94a3b8', minWidth: 32, textAlign: 'center' }}>
                {isInitialLoad ? <Skeleton variant="text" width={18} height={18} sx={{ mx: 'auto', bgcolor: alpha('#fff', 0.6) }} /> : <Typography sx={{ fontSize: '0.85rem', fontWeight: 800, lineHeight: 1.1 }}>{count}</Typography>}
              </Box>
            </Box>
          );
        })}
      </Box>

      {loading && !hasData && ['FORMING', 'MATURE'].map((stage) => {
        const meta = stageMetaMap[stage];
        const StageIcon = meta.Icon;
        return (
          <Paper key={`skel-${stage}`} elevation={0} sx={{ borderRadius: 1.6, border: `1px solid ${alpha(meta.color, 0.2)}`, bgcolor: '#fff', overflow: 'hidden' }}>
            <Box sx={{ px: 1.8, py: 1.2, borderBottom: `1px solid ${C.border}`, background: `linear-gradient(90deg, ${alpha(meta.color, 0.08)} 0%, ${alpha(meta.color, 0.01)} 100%)`, display: 'flex', alignItems: 'center', gap: 1.2 }}>
              <StageIcon sx={{ fontSize: 20, color: meta.color }} />
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: '0.88rem', fontWeight: 800, color: C.ink, letterSpacing: '-0.01em' }}>{meta.label}</Typography>
                <Typography sx={{ fontSize: '0.68rem', color: '#64748b', fontWeight: 500, mt: 0.2 }}>{meta.desc}</Typography>
              </Box>
            </Box>
            <Box sx={{ p: 1.4, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)' }, gap: 1 }}>
              {stageSkeletonSlots.map((slot) => (
                <Box key={`${stage}-${slot}`} sx={{ p: 1.2, borderRadius: 1.2, border: `1px solid ${C.border}`, bgcolor: '#fff', display: 'flex', flexDirection: 'column', gap: 0.6 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.4 }}>
                    <Skeleton variant="text" width={48} height={22} />
                    <Skeleton variant="rounded" width={52} height={20} />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.4 }}>
                    <Skeleton variant="text" width={88} height={14} />
                    <Skeleton variant="text" width={42} height={14} />
                  </Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.6 }}>
                    <Skeleton variant="rounded" width="100%" height={36} />
                    <Skeleton variant="rounded" width="100%" height={36} />
                  </Box>
                </Box>
              ))}
            </Box>
          </Paper>
        );
      })}

      {['FORMING', 'MATURE', 'BREAKOUT'].map((stage) => {
        const meta = stageMetaMap[stage];
        const StageIcon = meta.Icon;
        const items = pipeline[stage] || [];
        if (items.length === 0) return null;
        return (
          <Paper key={stage} elevation={0} sx={{ borderRadius: 1.6, border: `1px solid ${alpha(meta.color, 0.2)}`, bgcolor: '#fff', overflow: 'hidden' }}>
            <Box sx={{ px: 1.8, py: 1.2, borderBottom: `1px solid ${C.border}`, background: `linear-gradient(90deg, ${alpha(meta.color, 0.08)} 0%, ${alpha(meta.color, 0.01)} 100%)`, display: 'flex', alignItems: 'center', gap: 1.2 }}>
              <StageIcon sx={{ fontSize: 20, color: meta.color }} />
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: '0.88rem', fontWeight: 800, color: C.ink, letterSpacing: '-0.01em' }}>
                  {meta.label} <Box component="span" sx={{ color: meta.color, ml: 0.5 }}>· {items.length}</Box>
                </Typography>
                <Typography sx={{ fontSize: '0.68rem', color: '#64748b', fontWeight: 500, mt: 0.2 }}>{meta.desc}</Typography>
              </Box>
            </Box>
            <Box sx={{ p: 1.4, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)' }, gap: 1 }}>
              {items.map((row) => <StockCard key={row.ticker} row={row} stage={stage} stageMeta={meta} locale={locale} language={language} onSelectRow={onSelectRow} t={t} />)}
            </Box>
          </Paper>
        );
      })}

      {pipeline.FORMING.length === 0 && pipeline.MATURE.length === 0 && pipeline.BREAKOUT.length === 0 && !loading && (
        <Box sx={{ p: 4, textAlign: 'center', borderRadius: 1.4, border: `1px dashed ${C.border}`, bgcolor: '#fafbfc' }}>
          <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#64748b' }}>{t('earlyAccV2.empty.title')}</Typography>
          <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8', mt: 0.4 }}>{t('earlyAccV2.empty.hint')}</Typography>
        </Box>
      )}

      {pipeline.NEAR_MISS.length > 0 && (
        <Box sx={{ p: 1.4, borderRadius: 1.4, border: `1px dashed ${alpha(C.amber, 0.4)}`, bgcolor: alpha(C.amber, 0.03) }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.85 }}>
            <Typography sx={{ fontSize: '0.78rem', fontWeight: 800, color: C.amber, display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
              <VisibilityIcon sx={{ fontSize: 16 }} />{t('earlyAccV2.nearMiss.title', { count: pipeline.NEAR_MISS.length })}
            </Typography>
            <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8' }}>{t('earlyAccV2.nearMiss.subtitle')}</Typography>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6 }}>
            {pipeline.NEAR_MISS.slice(0, 24).map((row) => (
              <Box
                key={row.ticker}
                onClick={() => onSelectRow(row)}
                sx={{
                  display: 'inline-flex', alignItems: 'center', gap: 0.5,
                  px: 0.85, py: 0.4, borderRadius: 0.6,
                  bgcolor: '#fff', border: `1px solid ${C.border}`,
                  cursor: 'pointer', transition: 'all 0.15s',
                  '&:hover': { borderColor: C.amber, bgcolor: alpha(C.amber, 0.05) },
                }}
              >
                <Typography sx={{ fontSize: '0.74rem', fontWeight: 800, color: C.ink }}>{row.ticker}</Typography>
                <Typography sx={{ fontSize: '0.62rem', color: C.amber, fontWeight: 700 }}>{row.diagnostics?.criteria_passed || 0}/9</Typography>
                <Typography sx={{ fontSize: '0.6rem', color: '#94a3b8', fontWeight: 600 }}>{Number(row.early_accumulation_score).toFixed(0)}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </>
  );
}

PipelineSection.propTypes = {
  promotions: PropTypes.arrayOf(PropTypes.shape({ row: candidateRowPropType.isRequired, from: PropTypes.string, to: PropTypes.string })).isRequired,
  stageMetaMap: PropTypes.object.isRequired,
  pipeline: PropTypes.shape({
    FORMING: PropTypes.arrayOf(candidateRowPropType),
    MATURE: PropTypes.arrayOf(candidateRowPropType),
    BREAKOUT: PropTypes.arrayOf(candidateRowPropType),
    NEAR_MISS: PropTypes.arrayOf(candidateRowPropType),
  }).isRequired,
  loading: PropTypes.bool.isRequired,
  hasData: PropTypes.bool.isRequired,
  onSelectRow: PropTypes.func.isRequired,
  language: PropTypes.string.isRequired,
  locale: PropTypes.string.isRequired,
  t: PropTypes.func.isRequired,
};

export function DetailedTableSection({ candidates, loading, stageMetaMap, locale, language, onSelectRow, t }) {
  return (
    <SectionCard>
      <SectionHeader title={t('earlyAccV2.table.title')} />
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {[
                { key: t('earlyAccV2.table.cols.ticker') },
                { key: t('earlyAccV2.table.cols.score') },
                {
                  key: t('earlyAccV2.table.cols.aiConf', 'AI'),
                  tip: t(
                    'earlyAccV2.table.aiConfTip',
                    'AI confidence \u2014 pattern reliability win probability',
                  ),
                },
                { key: t('earlyAccV2.table.cols.delta'), tip: t('earlyAccV2.table.deltaTip') },
                { key: t('earlyAccV2.table.cols.stage') },
                { key: t('earlyAccV2.table.cols.tier'), tip: t('earlyAccV2.table.tierTip') },
                { key: t('earlyAccV2.table.cols.sector') },
                { key: t('earlyAccV2.table.cols.price') },
                { key: t('earlyAccV2.table.cols.dist') },
                { key: t('earlyAccV2.table.cols.accum') },
                { key: t('earlyAccV2.table.cols.rs20') },
                { key: t('earlyAccV2.table.cols.rs60') },
                { key: t('earlyAccV2.table.cols.str') },
              ].map((col) => (
                <TableCell key={col.key} sx={thSx}>
                  {col.tip ? (
                    <Tooltip arrow placement="top" title={col.tip}>
                      <Box component="span" sx={{ borderBottom: '1px dotted #94a3b8', cursor: 'help' }}>{col.key}</Box>
                    </Tooltip>
                  ) : col.key}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {candidates.map((row) => {
              const stage = row.accumulation_stage || 'NONE';
              const stageMeta = stageMetaMap[stage] || { color: C.slate, label: stage };
              const distRes = row.price?.dist_from_resistance;
              const accumRatio = row.metrics?.accumulation_ratio;
              const change = row.change || {};
              const delta = change.delta;
              const isNew = change.is_new;
              let deltaContent = <Typography sx={{ fontSize: '0.7rem', color: '#cbd5e1' }}>-</Typography>;
              if (isNew) {
                deltaContent = <Chip size="small" label={t('earlyAccV2.table.newBadge')} sx={{ height: 18, fontSize: '0.6rem', fontWeight: 800, bgcolor: alpha(C.emerald, 0.14), color: C.emerald }} />;
              } else if (delta != null) {
                deltaContent = <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: getDeltaColor(delta), fontFamily: 'inherit' }}>{delta > 0 ? '+' : ''}{Number(delta).toFixed(2)}</Typography>;
              }
              return (
                <TableRow key={`${row.ticker}-${row.score_date}`} onClick={() => onSelectRow(row)} sx={trSx} hover>
                  <TableCell sx={tdSx}><Typography sx={{ fontWeight: 800, color: C.blue }}>{row.ticker}</Typography></TableCell>
                  <TableCell sx={{ ...tdSx, fontWeight: 800, color: stageMeta.color }}>{fmtScore(row.early_accumulation_score, locale)}</TableCell>
                  <TableCell sx={tdSx}>
                    <AIConfidenceBadge
                      confidence={row.ai_confidence}
                      language={language}
                      tooltipTitle={t(
                        'earlyAccV2.table.aiConfTip',
                        'AI confidence \u2014 pattern reliability win probability',
                      )}
                      compact
                    />
                  </TableCell>
                  <TableCell sx={tdSx}>{deltaContent}</TableCell>
                  <TableCell sx={tdSx}><Chip size="small" label={stageMeta.label || stage} sx={{ fontWeight: 700, fontSize: '0.62rem', bgcolor: alpha(stageMeta.color, 0.12), color: stageMeta.color }} /></TableCell>
                  <TableCell sx={tdSx}>{row.tier && (() => { const tierMeta = getTierMeta(row.tier, t); return (
                    <Tooltip arrow placement="right" title={t('earlyAccV2.drawer.tierChip', { tier: row.tier, label: tierMeta.label, score: tierMeta.score, desc: tierMeta.desc })}>
                      <Chip size="small" label={`${row.tier} · ${tierMeta.label}`} sx={{ fontWeight: 800, fontSize: '0.66rem', bgcolor: alpha(tierMeta.color, 0.12), color: tierMeta.color, cursor: 'help' }} />
                    </Tooltip>
                  ); })()}</TableCell>
                  <TableCell sx={tdSx}><Typography sx={{ fontSize: '0.72rem' }}>{sectorDisplay(row, language)}</Typography></TableCell>
                  <TableCell sx={{ ...tdSx, fontWeight: 700 }}>{fmtInteger(row.price?.close, locale)}</TableCell>
                  <TableCell sx={{ ...tdSx, fontWeight: 700, color: hasValue(distRes) && distRes > 0 ? C.amber : C.emerald }}>{formatIfPresent(distRes, (value) => fmtPct(value, locale, 1))}</TableCell>
                  <TableCell sx={{ ...tdSx, fontWeight: 700, color: hasValue(accumRatio) && accumRatio > 1.2 ? C.emerald : C.amber }}>{formatIfPresent(accumRatio, (value) => fmtScore(value, locale))}</TableCell>
                  <TableCell sx={{ ...tdSx, fontSize: '0.7rem', fontFamily: 'inherit' }}>{fmtPct(row.metrics?.rs20, locale)}</TableCell>
                  <TableCell sx={{ ...tdSx, fontSize: '0.7rem', fontFamily: 'inherit' }}>{fmtPct(row.metrics?.rs60, locale)}</TableCell>
                  <TableCell sx={{ ...tdSx, fontWeight: 700 }}>{formatIfPresent(row.strength?.strength_score, (value) => fmtScore(value, locale, 1))}</TableCell>
                </TableRow>
              );
            })}
            {loading && candidates.length === 0 && tableSkeletonRows.map((rowKey) => (
              <TableRow key={`skel-${rowKey}`}>
                {tableSkeletonColumns.map((columnKey, columnIndex) => (
                  <TableCell key={`${rowKey}-${columnKey}`} sx={tdSx}>
                    <Skeleton variant="text" width={getSkeletonWidth(columnIndex)} height={18} />
                  </TableCell>
                ))}
              </TableRow>
            ))}
            {!loading && candidates.length === 0 && (
              <TableRow>
                <TableCell colSpan={13} sx={{ textAlign: 'center', py: 4, color: '#94a3b8' }}>{t('dashboard.earlyAccumulation.alerts.noRows')}</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </SectionCard>
  );
}

DetailedTableSection.propTypes = {
  candidates: PropTypes.arrayOf(candidateRowPropType).isRequired,
  loading: PropTypes.bool.isRequired,
  stageMetaMap: PropTypes.object.isRequired,
  locale: PropTypes.string.isRequired,
  language: PropTypes.string.isRequired,
  onSelectRow: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

export function DashboardHero({ data, scanDateValue, marketRegime, calculating, loading, onCalculate, t }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ width: 38, height: 38, borderRadius: 1.4, background: 'linear-gradient(135deg, #38bdf8 0%, #6366f1 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 14px rgba(99, 102, 241, 0.25)' }}>
          <BubbleChartIcon sx={{ fontSize: 20, color: '#fff' }} />
        </Box>
        <Box>
          <Typography sx={{ fontSize: '1.18rem', fontWeight: 800, color: C.ink, letterSpacing: -0.3, lineHeight: 1.2 }}>{t('earlyAccV2.hero.title')}</Typography>
          <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8', fontWeight: 500 }}>{t('earlyAccV2.hero.subtitle')}</Typography>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        {data?.score_date && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, bgcolor: '#f1f5f9', px: 1.1, py: 0.45, borderRadius: 1.5 }}>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: C.emerald, boxShadow: `0 0 6px ${C.emerald}` }} />
            <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#475569' }}>{scanDateValue}</Typography>
          </Box>
        )}
        {marketRegime && (
          <Box sx={{ px: 1.1, py: 0.45, borderRadius: 1.5, bgcolor: alpha(reliabilityTone(marketRegime === 'SIDEWAY' ? 75 : 60), 0.1), border: `1px solid ${alpha(reliabilityTone(marketRegime === 'SIDEWAY' ? 75 : 60), 0.25)}` }}>
            <Typography sx={{ fontSize: '0.65rem', fontWeight: 800, color: reliabilityTone(marketRegime === 'SIDEWAY' ? 75 : 60), letterSpacing: '0.04em' }}>
              {t('earlyAccV2.marketRegime', { regime: marketRegime })}
            </Typography>
          </Box>
        )}
        <Button variant="outlined" size="small" startIcon={calculating || loading ? <CircularProgress size={12} /> : null} onClick={onCalculate} disabled={calculating || loading} sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.72rem' }}>
          {calculating ? t('earlyAccV2.hero.refreshing') : t('earlyAccV2.hero.refresh')}
        </Button>
      </Box>
    </Box>
  );
}

DashboardHero.propTypes = {
  data: PropTypes.shape({ score_date: PropTypes.string }),
  scanDateValue: PropTypes.string.isRequired,
  marketRegime: PropTypes.string,
  calculating: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
  onCalculate: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

export function DashboardFilters({ filters, setFilters, loading, showHistory, setShowHistory, showAdvanced, setShowAdvanced, onApply, t }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 0.8, flexWrap: 'wrap' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7, px: 1, height: 32, borderRadius: 0.8, bgcolor: '#f8fafc', border: `1px solid ${C.border}` }}>
        <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{'Score >='}</Typography>
        <Box component="input" type="number" value={filters.min_score} onChange={(event) => setFilters((previous) => ({ ...previous, min_score: Number(event.target.value || 0) }))} sx={{ width: 44, border: 'none', outline: 'none', bgcolor: 'transparent', fontSize: '0.8rem', fontWeight: 800, color: C.ink, fontFamily: 'inherit', textAlign: 'center', p: 0, m: 0, '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': { WebkitAppearance: 'none', m: 0 }, MozAppearance: 'textfield' }} />
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7, px: 1, height: 32, borderRadius: 0.8, bgcolor: '#f8fafc', border: `1px solid ${C.border}`, flex: { xs: '1 1 100%', sm: '0 0 200px' } }}>
        <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('earlyAccV2.filters.sector')}</Typography>
        <Box component="input" type="text" placeholder={t('earlyAccV2.filters.sectorPlaceholder')} value={filters.sector} onChange={(event) => setFilters((previous) => ({ ...previous, sector: event.target.value }))} sx={{ flex: 1, minWidth: 0, border: 'none', outline: 'none', bgcolor: 'transparent', fontSize: '0.78rem', fontWeight: 600, color: C.ink, fontFamily: 'inherit', p: 0, m: 0, '&::placeholder': { color: '#cbd5e1', fontWeight: 500 } }} />
      </Box>

      <Box onClick={() => setFilters((previous) => ({ ...previous, strong_only: !previous.strong_only }))} sx={{ display: 'flex', alignItems: 'center', gap: 0.7, px: 1.1, height: 32, borderRadius: 0.8, cursor: 'pointer', bgcolor: filters.strong_only ? alpha(C.blue, 0.1) : '#f8fafc', border: `1px solid ${filters.strong_only ? alpha(C.blue, 0.4) : C.border}`, transition: 'all 0.15s', '&:hover': { bgcolor: filters.strong_only ? alpha(C.blue, 0.14) : '#f1f5f9' } }}>
        <Box sx={{ width: 26, height: 14, borderRadius: 7, bgcolor: filters.strong_only ? C.blue : '#cbd5e1', position: 'relative', transition: 'all 0.2s', flexShrink: 0 }}>
          <Box sx={{ position: 'absolute', top: 1, left: filters.strong_only ? 13 : 1, width: 12, height: 12, borderRadius: '50%', bgcolor: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.2)', transition: 'all 0.2s' }} />
        </Box>
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: filters.strong_only ? C.blue : '#64748b' }}>{t('earlyAccV2.filters.strongOnly')}</Typography>
      </Box>

      <Button size="small" variant="contained" onClick={onApply} disabled={loading} sx={{ textTransform: 'none', fontWeight: 800, fontSize: '0.74rem', height: 32, px: 1.6, minWidth: 0, boxShadow: 'none', '&:hover': { boxShadow: 'none' } }}>{t('earlyAccV2.filters.apply')}</Button>

      <Box sx={{ flexGrow: 1 }} />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
        <Button size="small" variant={showHistory ? 'outlined' : 'text'} onClick={() => setShowHistory((value) => !value)} sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.7rem', height: 32, color: showHistory ? C.blue : '#64748b' }}>
          {showHistory ? <><CheckRoundedIcon sx={{ fontSize: 13, mr: 0.4 }} />{t('earlyAccV2.filters.history')}</> : t('earlyAccV2.filters.history')}
        </Button>
        <Button size="small" variant={showAdvanced ? 'outlined' : 'text'} onClick={() => setShowAdvanced((value) => !value)} sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.7rem', height: 32, color: showAdvanced ? C.blue : '#64748b' }}>
          {showAdvanced ? <><CheckRoundedIcon sx={{ fontSize: 13, mr: 0.4 }} />{t('earlyAccV2.filters.advanced')}</> : t('earlyAccV2.filters.advanced')}
        </Button>
      </Box>
    </Box>
  );
}

DashboardFilters.propTypes = {
  filters: PropTypes.shape({ min_score: PropTypes.number.isRequired, sector: PropTypes.string.isRequired, strong_only: PropTypes.bool.isRequired }).isRequired,
  setFilters: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  showHistory: PropTypes.bool.isRequired,
  setShowHistory: PropTypes.func.isRequired,
  showAdvanced: PropTypes.bool.isRequired,
  setShowAdvanced: PropTypes.func.isRequired,
  onApply: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};
